import { useQuery } from '@apollo/client';
import { useOktaAuth } from '@okta/okta-react';
import { GET_TALENT_ACCESS } from '../graphql/talent.queries';
import { ITalentAccess } from '../interfaces/ITalent';

interface UseTalentAccessResult extends ITalentAccess {
  loading: boolean;
  /** Whether the Skill Matrix entry should appear at all for this user. */
  canSee: boolean;
  /** Show the pending dot: they can see the module and still owe the form. */
  showPending: boolean;
}

/**
 * Single source of truth for Skill Matrix visibility, shared by the navbar
 * and the page itself so the menu never advertises something the page would
 * then refuse to render.
 *
 * The rule mirrors the server: administrators always get in, everybody else
 * needs a profile plus permission to use the module — either SKILLS_VIEWER,
 * which marks technical staff, or the module having gone public.
 */
export function useTalentAccess(): UseTalentAccessResult {
  const { authState } = useOktaAuth();
  const { data, loading } = useQuery<{ talentAccess: ITalentAccess }>(
    GET_TALENT_ACCESS,
    { skip: !authState?.isAuthenticated, fetchPolicy: 'cache-first' },
  );

  const access = data?.talentAccess;
  const hasProfile = !!access?.hasProfile;
  const isAdmin = !!access?.isAdmin;
  const moduleEnabled = !!access?.moduleEnabled;
  const canUse = !!access?.canUse;
  const needsSkillOnboarding = !!access?.needsSkillOnboarding;
  const isManager = !!access?.isManager;
  const formOnly = !!access?.formOnly;
  // Managers get in to follow their team even without a profile of their own.
  const canSee = !formOnly && (isAdmin || isManager || (hasProfile && canUse));

  return {
    hasProfile,
    isAdmin,
    moduleEnabled,
    canUse,
    needsSkillOnboarding,
    skillsOnboardingStep: access?.skillsOnboardingStep ?? 0,
    profileId: access?.profileId ?? null,
    isManager,
    reportCount: access?.reportCount ?? 0,
    formOnly,
    unansweredCount: access?.unansweredCount ?? 0,
    // True until the server says otherwise: most of the office is Costa Rica,
    // and nobody should wait on this to see the map.
    deskbookingEnabled: access?.deskbookingEnabled ?? true,
    loading,
    canSee,
    // Only worth a dot if they can actually get to the form to clear it.
    showPending: canSee && needsSkillOnboarding,
  };
}
